import axios from 'axios';
import { getToken } from './utils';
import { dispatchApiError, extractFieldErrors } from '../helpers/apiErrorEvent';
import { trackException } from './appInsights';

const baseURL = process.env.REACT_APP_BASE_URL;
let isRefreshing = false;
let refreshSubscribers: any = [];

const token = localStorage.getItem('token');

// Classifies browser-level network failures (no HTTP response was ever received)
// into the buckets App Insights users actually search for.
const classifyNetworkError = (error: any): string => {
  const code = error?.code || '';
  const message: string = error?.message || '';

  if (code === 'ERR_NAME_NOT_RESOLVED' || message.includes('ERR_NAME_NOT_RESOLVED')) return 'DNS Failure';
  if (code === 'ECONNABORTED' || code === 'ERR_CONNECTION_TIMED_OUT' || message.includes('timeout')) return 'Corporate Firewall (Timeout)';
  if (code === 'ERR_CERT_AUTHORITY_INVALID' || message.includes('ERR_CERT_AUTHORITY_INVALID')) return 'SSL Block';
  if (message === 'Network Error') return 'CORS Failure / Blocked Request';
  return 'Unknown Network Error';
};

const axiosInstance = axios.create({
  baseURL: baseURL,
  headers: {
    'Authorization': token ? `Bearer ${token}` : '',
    "content-type": "application/json"
  },
  responseType: "json"
});

axiosInstance.interceptors.request.use(
  (config: any) => {
    if (localStorage.getItem('token') != null) {
      const token = localStorage.getItem('token');
      config.headers.Authorization = token ? `Bearer ${token}` : null;
    }
    return config;
  }
);
axiosInstance.interceptors.response.use((response) => {
  return response;
}, (error) => {
  // Network errors (timeout, CORS, offline) have no response object.
  // Return a normalised object so callers never crash on res.status.
  if (!error.response) {
    trackException(error, {
      source: 'axios',
      kind: 'network',
      reason: classifyNetworkError(error),
      url: error?.config?.url,
      method: error?.config?.method,
      code: error?.code,
    });
    return Promise.resolve({
      status: 503,
      data: `503 - ${error?.message || 'Network error. Please check your connection and try again.'}`
    });
  }

  const { config, response: { status } } = error;
  const originalRequest = config;

  if (status === 401) {
    if (!isRefreshing) {
      isRefreshing = true;
      getToken()
        .then(newToken => {
          isRefreshing = false;
          onRrefreshed(newToken);
        })
        .catch(() => {
          // Refresh failed — clear queued subscribers, wipe session and redirect.
          isRefreshing = false;
          refreshSubscribers = [];
          localStorage.clear();
          window.location.href = '/';
        });
    }

    const retryOrigReq = new Promise((resolve) => {
      subscribeTokenRefresh((token: any) => {
        // replace the expired token and retry
        originalRequest.headers['Authorization'] = 'Bearer ' + token;
        resolve(axios(originalRequest));
      });
    });
    return retryOrigReq;
  }

  // For all non-401 HTTP errors, reject with a normalised Error object instead
  // of the raw Axios error.
  //
  // WHY reject (not resolve):
  //   Resolving with an error-shaped object means components that omit error
  //   checks will call setState with the raw API error body (e.g. ASP.NET
  //   ProblemDetails: { errors, type, title, status, traceId }).  When that
  //   object is rendered as a React child the app throws "Objects are not valid
  //   as a React child" and crashes.
  //
  // WHY a normalised Error (not the raw Axios error):
  //   Components with .catch() / try-catch get a clean, predictable shape.
  //   Components that omit error handling fire an `unhandledrejection` event;
  //   the ErrorBoundary listener (event.preventDefault()) suppresses the
  //   browser overlay and logs it — the UI stays alive.

  const responseData = error.response?.data;

  // Extract a safe string message from common API error shapes:
  //   ASP.NET Core ProblemDetails  → { title, status, traceId, errors }
  //   Generic JSON                 → { message } / { Message } / { error }
  //   Plain string body            → "Some error text"
  const extractMessage = (data: any, httpStatus: number): string => {
    if (!data)                             return `HTTP ${httpStatus}`;
    if (typeof data === 'string')          return data;
    if (typeof data?.title === 'string')   return data.title;
    if (typeof data?.message === 'string') return data.message;
    if (typeof data?.Message === 'string') return data.Message;
    if (typeof data?.error === 'string')   return data.error;
    const fallbacks: Record<number, string> = {
      400: 'Bad request. Please check the submitted data.',
      403: 'You do not have permission to perform this action.',
      404: 'The requested resource was not found.',
      408: 'The request timed out. Please try again.',
      409: 'A conflict occurred. The resource may have already been modified.',
      422: 'Validation failed. Please check the submitted data.',
      429: 'Too many requests. Please wait a moment and try again.',
      500: 'An internal server error occurred. Please try again later.',
      502: 'Bad gateway. The server is temporarily unavailable.',
      503: 'The service is temporarily unavailable. Please try again later.',
      504: 'The server did not respond in time. Please try again.',
    };
    return fallbacks[httpStatus] ?? `An unexpected error occurred (HTTP ${httpStatus}).`;
  };

  const message = extractMessage(responseData, status);
  const fieldErrors = extractFieldErrors(responseData);
  // Prefix with the status code so every consumer (global snackbar, local
  // catch blocks reading error.message/response.data) shows "status - message".
  const messageWithStatus = `${status} - ${message}`;

  // Fire the global snackbar event so the ApiErrorSnackbar (mounted in
  // App.tsx) shows the error without requiring every component to handle it.
  dispatchApiError({ message: messageWithStatus, fieldErrors, status });

  // console.error(`[Interceptor] HTTP ${status}:`, responseData ?? error.message);

  const apiError: any = new Error(messageWithStatus);
  apiError.status = status;
  apiError.data = responseData ?? null;
  apiError.isApiError = true;
  apiError.fieldErrors = fieldErrors;

  trackException(apiError, {
    source: 'axios',
    kind: 'http',
    status,
    url: config?.url,
    method: config?.method,
  });

  return Promise.reject(apiError);
})

const subscribeTokenRefresh = (cb: any) => {
  refreshSubscribers.push(cb);
}

const onRrefreshed = (token: any) => {
  refreshSubscribers && refreshSubscribers.map((cb: any) => cb(token));
  while (refreshSubscribers.length > 0) {
    refreshSubscribers.pop();
  }

}

export default axiosInstance;